import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import BasicModal from '__components/Modals/presets/BasicModal';
import { useToast } from '__components/ToastProvider';
import KnowledgeService from 'services/KnowledgeService';
import { getErrorMessage } from '__hooks/useErrors';
import { getOriginalError } from '__services/AxiosService';
import { useAsync } from 'react-use';
import Loader from '__components/Kizen/Loader';
import { markdownToHtml } from '@kizen/parse-md';
import { StyledMarkdownContent } from '../styles';

type PageContentModalProps = {
  pageId: string;
  catalogId: string;
  onHide: () => void;
};

export const PageContentModal: FC<PageContentModalProps> = ({
  pageId,
  catalogId,
  onHide,
}) => {
  const { t } = useTranslation();
  const [showToast, toastVariant] = useToast();

  const { value, loading = true } = useAsync(async () => {
    try {
      const details = await KnowledgeService.getOnlineSourcePageDetails(pageId, {
        params: { catalog_id: catalogId },
      });

      return markdownToHtml(details.markdown_content || '', {
        forceExternalLinks: true,
      });
    } catch (error) {
      showToast({
        message:
          getErrorMessage(getOriginalError(error)) ||
          t('Error fetching page content'),
        variant: toastVariant.FAILURE,
      });
      onHide();
      return null;
    }
  }, [pageId]);

  return (
    <BasicModal
      show
      heading={t('Page Content')}
      leftBtn={null}
      buttonText={t('Close')}
      actionBtnColor="blue"
      size="large"
      onHide={onHide}
      onConfirm={onHide}
    >
      <Loader loading={loading}>
        <StyledMarkdownContent
          dangerouslySetInnerHTML={{ __html: value || '' }}
        />
      </Loader>
    </BasicModal>
  );
};
